import { Spacing } from '@persona-id/design-tokens';
import { intersection } from 'lodash';
import { capitalize, keyBy } from 'lodash/fp';
import { useSelector } from 'react-redux';
import { Field, getFormValues, InjectedFormProps, reduxForm } from 'redux-form';
import styled from 'styled-components';

import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import TextAreaField from 'dashboard/components/FormControls/TextAreaField';
import Section from 'dashboard/layouts/FormLayout/Section/Section';
import { CaseState, CaseTemplateBasicsOption } from 'dashboard/models/case-template';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { isRequired, withMessage } from 'lib/utils/formValidation';

const Label = styled('h4')`
  margin-bottom: ${Spacing[2]};
`;

const validate = {
  status: [withMessage('Please select a status', isRequired)],
  message: [withMessage('Please enter a comment.', isRequired)],
};

interface FormBulkSetCaseStateProps {
  handleCancel: () => void;
  selectedStates: CaseState[];
  caseTemplateStates: CaseTemplateBasicsOption['states'];
}

const FormBulkSetCaseState = ({
  handleCancel,
  handleSubmit,
  selectedStates,
  caseTemplateStates,
  submitting,
}: FormBulkSetCaseStateProps & InjectedFormProps<unknown, FormBulkSetCaseStateProps>) => {
  const stateMap = caseTemplateStates ? keyBy((s) => s.token, caseTemplateStates) : {};
  const stateTokens = intersection(...selectedStates.map((state) => state['next-state-ids']));

  const stateOptions = stateTokens.map((token) => ({
    label: capitalize(stateMap[token].name),
    value: token,
  }));

  const formValues = useSelector((state) =>
    getFormValues('bulkSetCaseState')(state),
  ) as BulkSetCaseStateData;

  const mandatoryComment = stateMap[formValues?.stateId]?.['comment-required'];

  return (
    <form onSubmit={handleSubmit}>
      <Section>
        <Label>Status</Label>
        <Field
          component={ReactSelectField}
          name='stateId'
          id='stateId'
          placeholder='Select a status'
          disabled={submitting}
          options={stateOptions}
          validate={validate.status}
          isSearchable={true}
          isClearable={true}
          portal={true}
        />

        <Label>Comment ({mandatoryComment ? 'required' : 'optional'})</Label>
        <Field
          component={TextAreaField}
          name='message'
          id='message'
          disabled={submitting}
          validate={mandatoryComment ? validate.message : null}
        />
      </Section>

      <HStack equal={true}>
        <Button
          id='form-bulk-set-case-state__cancel'
          data-test='form-bulk-set-case-state__cancel'
          disabled={submitting}
          variant='basic'
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          disabled={submitting}
          data-test='form-bulk-set-case-state__submit'
          isPending={submitting}
        >
          Update
        </Button>
      </HStack>
    </form>
  );
};

export interface BulkSetCaseStateData {
  stateId: string;
  message?: string;
}

export default reduxForm<BulkSetCaseStateData, FormBulkSetCaseStateProps>({
  form: 'bulkSetCaseState',
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormBulkSetCaseState);
